import { api } from "aldoo-ra/API"
import { v4 as uuidv4 } from "uuid"
import schema from "./update-schema"

const devOpsAPI = api("aldoo-devops")
const taskAPI = api("aldoo-task")

// Default timeout and polling intervals (in milliseconds)
const DEFAULT_TIMEOUT = 5 * 60 * 1000 // 5 minutes
const DEFAULT_POLL_INTERVAL = 2000 // 2 seconds

// Operation specific timeouts and polling intervals
const OPERATION_CONFIG = {
  UPDATE_CREATE: {
    timeout: 20 * 60 * 1000, // 20 minutes
    pollInterval: 5000, // 5 seconds
  },
  UPDATE_LOAD: {
    timeout: 30 * 60 * 1000, // 30 minutes
    pollInterval: 5000, // 5 seconds
  },
}

// Create a singleton instance
let instance = null

/**
 * This controller is handling the UpdateConfiguration objects
 */
function createController() {
  // Return existing instance if available
  if (instance) return instance

  //This array stores all the UpdateConfiguration objects
  //in the schema format
  const items = []
  //the global include and exclude arrays
  //contain object identifiers that are used to select data/files
  //id, file path, etc.
  //This will be applied to the dependent objects
  const include = []
  const exclude = []

  // Create event dispatcher for state changes
  const eventDispatcher = new EventTarget()

  /**
   * Polls task status until completion or timeout
   */
  const pollTaskStatus = async ({
    response,
    timeout = DEFAULT_TIMEOUT,
    pollInterval = DEFAULT_POLL_INTERVAL,
    onResponse,
  }) => {
    if (response.error || !response.task) {
      return response
    }

    const startTime = Date.now()
    const taskId = response.task

    while (true) {
      if (Date.now() - startTime > timeout) {
        return { error: "timeout" }
      }

      const response = await taskAPI({
        get: {
          id: taskId,
          result: true,
          log: true,
        },
      })

      if (onResponse) onResponse(response)

      if (response.error) return response

      if (
        response.status === "completed" ||
        response.status === "failed" ||
        response.status === "error"
      ) {
        return response
      }

      await new Promise((resolve) => setTimeout(resolve, pollInterval))
    }
  }

  /**
   * Create a new UpdateConfiguration object
   * @returns {UpdateConfiguration}
   */
  const create = () => {
    return {
      ...schema,
      id: uuidv4(),
    }
  }

  //get the UpdateConfiguration object by id
  const get = (id) => {
    return items.find((item) => item.id === id)
  }

  //Add or edit the UpdateConfiguration object
  const add = (updateObject) => {
    const index = items.findIndex((item) => item.id === updateObject.id)
    //edit the object
    if (index !== -1) items[index] = updateObject
    else items.push(updateObject)

    // Dispatch state change event
    eventDispatcher.dispatchEvent(new Event("stateChanged"))
  }

  //Remove the UpdateConfiguration object by id
  const remove = (id) => {
    const index = items.findIndex((item) => item.id === id)
    if (index === -1) return
    items.splice(index, 1)

    // Dispatch state change event
    eventDispatcher.dispatchEvent(new Event("stateChanged"))
  }

  //get the items array
  const getItems = () => items

  const clearItems = () => {
    items.splice(0, items.length)
    eventDispatcher.dispatchEvent(new Event("stateChanged"))
  }

  const addInclude = (id) => {
    if (include.includes(id)) return
    include.push(id)
    eventDispatcher.dispatchEvent(new Event("stateChanged"))
  }

  const removeInclude = (id) => {
    const index = include.findIndex((item) => item === id)
    if (index === -1) return
    include.splice(index, 1)
    eventDispatcher.dispatchEvent(new Event("stateChanged"))
  }

  const addExclude = (id) => {
    if (exclude.includes(id)) return
    exclude.push(id)
    eventDispatcher.dispatchEvent(new Event("stateChanged"))
  }

  const removeExclude = (id) => {
    const index = exclude.findIndex((item) => item === id)
    if (index === -1) return
    exclude.splice(index, 1)
    eventDispatcher.dispatchEvent(new Event("stateChanged"))
  }

  /**
   * Create a new site update using controller items as target
   * @param {Object} options - Update creation options
   * @param {string} options.updateName - Name of the update
   * @param {string} options.description - Update description
   * @param {Function} options.onResponse - Optional callback for status updates
   * @returns {Promise<object>} - Creation task result or error
   */
  const createUpdate = async ({ updateName, description, onResponse }) => {
    //if target is an array of update objects
    const target = items.map((item) => {
      const targetObj = {
        type: item.type.value,
      }

      // Only include match if it's not empty
      if (item.query.match && Object.keys(item.query.match).length > 0) {
        targetObj.match = item.query.match
      }

      return targetObj
    })

    const response = await devOpsAPI({
      run: {
        script: "site-update-create",
        env: {
          updateName,
          description,
          target,
        },
      },
    })

    return await pollTaskStatus({
      response,
      timeout: OPERATION_CONFIG.UPDATE_CREATE.timeout,
      pollInterval: OPERATION_CONFIG.UPDATE_CREATE.pollInterval,
      onResponse,
    })
  }

  /**
   * Load a site update using its update key
   * @param {Object} options - Update loading options
   * @param {string} options.updateKey - The update key to load
   * @param {string} options.fileMode - File handling mode ('REPLACE' or other modes)
   * @param {string} options.dbMode - Database handling mode ('REPLACE' or other modes)
   * @param {Function} options.onResponse - Optional callback for status updates
   * @returns {Promise<object>} - Loading task result or error
   */
  const loadUpdate = async ({
    updateKey,
    fileMode = "REPLACE",
    dbMode = "REPLACE",
    onResponse,
  }) => {
    const response = await devOpsAPI({
      run: {
        script: "site-update-load",
        env: {
          "update-key": updateKey,
          FILE_MODE: fileMode,
          DB_MODE: dbMode,
        },
      },
    })

    return await pollTaskStatus({
      response,
      timeout: OPERATION_CONFIG.UPDATE_LOAD.timeout,
      pollInterval: OPERATION_CONFIG.UPDATE_LOAD.pollInterval,
      onResponse,
    })
  }

  /**
   * Upload and unpack a site update file with progress tracking
   * @param {Object} options - Upload options
   * @param {File} options.file - The update file to upload
   * @param {Function} options.setProgress - Progress callback
   * @param {Function} options.onResponse - Optional callback for status updates
   * @returns {Promise<object>} - Upload task result or error
   */
  const uploadUpdate = async ({ file, setProgress, onResponse }) => {
    const formData = new FormData()
    formData.append("file", file)
    formData.append("run", 1)
    formData.append("script", "site-update-unpack")

    const response = await devOpsAPI(formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress: (progressEvent) => {
        const progress = Math.round(
          (progressEvent.loaded / progressEvent.total) * 100
        )
        setProgress(progress)
      },
    })

    return await pollTaskStatus({
      response,
      timeout: OPERATION_CONFIG.UPDATE_LOAD.timeout,
      pollInterval: OPERATION_CONFIG.UPDATE_LOAD.pollInterval,
      onResponse,
    })
  }

  // Subscribe to state changes
  const subscribe = (callback) => {
    const handleChange = () => callback()
    eventDispatcher.addEventListener("stateChanged", handleChange)
    return () => {
      eventDispatcher.removeEventListener("stateChanged", handleChange)
    }
  }

  instance = {
    add,
    get,
    create,
    remove,
    getItems,
    clearItems,
    addInclude,
    removeInclude,
    addExclude,
    removeExclude,
    createUpdate,
    uploadUpdate,
    loadUpdate,
    subscribe,
  }

  return instance
}

export default createController
